/**
 * FormStateController - Centralized state management for wizard forms
 *
 * Uses Lit's ReactiveController pattern for automatic re-rendering
 * when state changes.
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit';

import type { FormState, FieldValidation, ValidationResult, StepConfig } from '../types.js';

type StateSubscriber = (state: FormState) => void;

export class FormStateController implements ReactiveController {
  host: ReactiveControllerHost;

  // Internal state
  private _formData: Record<string, unknown> = {};
  private _validation: Record<string, FieldValidation> = {};
  private _currentStep = 1;
  private _totalSteps = 0;
  private _submitting = false;
  private _submitted = false;
  private _error?: string;
  private _stepConfigs: Map<number, StepConfig> = new Map();
  private _subscribers: Set<StateSubscriber> = new Set();

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  // ============================================
  // Lifecycle
  // ============================================

  hostConnected(): void {
    // Controller connected to host
  }

  hostDisconnected(): void {
    // Clean up subscriptions
    this._subscribers.clear();
  }

  // ============================================
  // State Getters
  // ============================================

  get formData(): Record<string, unknown> {
    return { ...this._formData };
  }

  get validation(): Record<string, FieldValidation> {
    return { ...this._validation };
  }

  get currentStep(): number {
    return this._currentStep;
  }

  get totalSteps(): number {
    return this._totalSteps;
  }

  get submitting(): boolean {
    return this._submitting;
  }

  get submitted(): boolean {
    return this._submitted;
  }

  get error(): string | undefined {
    return this._error;
  }

  get state(): FormState {
    return {
      formData: this.formData,
      validation: this.validation,
      currentStep: this._currentStep,
      totalSteps: this._totalSteps,
      submitting: this._submitting,
      submitted: this._submitted,
      error: this._error,
    };
  }

  // ============================================
  // State Mutations
  // ============================================

  setValue(name: string, value: unknown): void {
    this._formData[name] = value;

    // Mark field as dirty
    if (this._validation[name]) {
      this._validation[name] = {
        ...this._validation[name],
        value,
        dirty: true,
      };
    } else {
      this._validation[name] = {
        name,
        value,
        result: { valid: true },
        touched: false,
        dirty: true,
      };
    }

    this._notifyChange();
  }

  getValue(name: string): unknown {
    return this._formData[name];
  }

  setValidation(name: string, result: ValidationResult): void {
    const existing = this._validation[name];
    this._validation[name] = {
      name,
      value: existing?.value ?? this._formData[name],
      result,
      touched: existing?.touched ?? false,
      dirty: existing?.dirty ?? false,
    };
    this._notifyChange();
  }

  markTouched(name: string): void {
    if (this._validation[name]) {
      this._validation[name] = {
        ...this._validation[name],
        touched: true,
      };
    } else {
      this._validation[name] = {
        name,
        value: this._formData[name],
        result: { valid: true },
        touched: true,
        dirty: false,
      };
    }
    this._notifyChange();
  }

  // ============================================
  // Step Management
  // ============================================

  setTotalSteps(total: number): void {
    this._totalSteps = total;
    this._notifyChange();
  }

  setStepConfig(step: number, config: StepConfig): void {
    this._stepConfigs.set(step, config);
  }

  getStepConfig(step: number): StepConfig | undefined {
    return this._stepConfigs.get(step);
  }

  getStepFields(step: number): string[] {
    return this._stepConfigs.get(step)?.fields ?? [];
  }

  goToStep(step: number): boolean {
    if (step < 1 || step > this._totalSteps) {
      return false;
    }

    // Check for skip conditions
    const config = this._stepConfigs.get(step);
    if (config?.skipIf?.(this._formData)) {
      // Skip this step, try next/previous
      if (step > this._currentStep) {
        return this.goToStep(step + 1);
      } else {
        return this.goToStep(step - 1);
      }
    }

    this._currentStep = step;
    this._notifyChange();
    return true;
  }

  nextStep(): boolean {
    return this.goToStep(this._currentStep + 1);
  }

  previousStep(): boolean {
    return this.goToStep(this._currentStep - 1);
  }

  isFirstStep(): boolean {
    return this._currentStep === 1;
  }

  isLastStep(): boolean {
    return this._currentStep === this._totalSteps;
  }

  // ============================================
  // Validation Helpers
  // ============================================

  isStepValid(step: number): boolean {
    const fields = this.getStepFields(step);
    return fields.every((name) => {
      const validation = this._validation[name];
      return !validation || validation.result.valid;
    });
  }

  isCurrentStepValid(): boolean {
    return this.isStepValid(this._currentStep);
  }

  getFieldError(name: string): string | undefined {
    const validation = this._validation[name];
    if (validation?.touched && !validation.result.valid) {
      return validation.result.error;
    }
    return undefined;
  }

  hasFieldError(name: string): boolean {
    return this.getFieldError(name) !== undefined;
  }

  // ============================================
  // Submission State
  // ============================================

  setSubmitting(submitting: boolean): void {
    this._submitting = submitting;
    this._notifyChange();
  }

  setSubmitted(submitted: boolean): void {
    this._submitted = submitted;
    this._notifyChange();
  }

  setError(error: string | undefined): void {
    this._error = error;
    this._notifyChange();
  }

  // ============================================
  // Reset
  // ============================================

  reset(): void {
    this._formData = {};
    this._validation = {};
    this._currentStep = 1;
    this._submitting = false;
    this._submitted = false;
    this._error = undefined;
    this._notifyChange();
  }

  // ============================================
  // Subscriptions
  // ============================================

  subscribe(callback: StateSubscriber): () => void {
    this._subscribers.add(callback);
    return () => {
      this._subscribers.delete(callback);
    };
  }

  private _notifyChange(): void {
    // Request host update
    this.host.requestUpdate();

    // Notify subscribers
    const state = this.state;
    this._subscribers.forEach((callback) => {
      try {
        callback(state);
      } catch (error) {
        console.error('[FormStateController] Subscriber error:', error);
      }
    });
  }
}
